/**
 * useElementSize - 容器尺寸自适应 Hook
 *
 * Measures an element's client size via ResizeObserver and returns a ref to
 * attach plus the current { width, height }. The size starts at { 0, 0 } and
 * updates whenever the element is laid out or resized.
 *
 * 通过 ResizeObserver 测量容器元素的 client 尺寸，返回需要绑定的 ref
 * 与当前的 { width, height }。初始值为 { 0, 0 }，元素布局完成或尺寸
 * 变化时自动更新。
 *
 * Usage / 用法：
 *   const { ref, size } = useElementSize<HTMLDivElement>();
 *   return <div ref={ref}>...</div>;
 *   // size.width / size.height
 */
import { useEffect, useRef, useState } from 'react';

export interface ElementSize {
  width: number;
  height: number;
}

/**
 * Measure a container element's client size via ResizeObserver.
 *
 * @returns `{ ref }` to attach to the target element and `{ size }` holding
 *          its current { width, height } (starts at { 0, 0 }).
 */
export function useElementSize<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [size, setSize] = useState<ElementSize>({ width: 0, height: 0 });

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () => {
      setSize({ width: el.clientWidth, height: el.clientHeight });
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return { ref, size };
}
